// Shared member card — used by both Members.jsx (desktop) and
// MembersMobile.jsx. Landscape layout with an amber painted-brush
// avatar panel on the left, name + contact + role/team pills in
// the middle, and a right column of stacked action buttons
// (Stats / Edit / Remove). At ≤640px it stacks vertically.

import React from "react";
import {
  Crown, Award, DollarSign, Trophy, Volleyball,
  User as UserIcon, Gavel, ShieldCheck, Shield,
  BadgeCheck, Users as UsersIcon,
  Mail, Phone as PhoneIcon,
  BarChart3, Pencil, Trash2
} from "lucide-react";
import Avatar from "./Avatar";
import { teamLogoSrc } from "./flyers/flyerHelpers";

const ROLE_META = {
  PRESIDENT:       { label: "President",       Icon: Crown       },
  VICE_PRESIDENT:  { label: "Vice President",  Icon: Award       },
  FINANCE_HEAD:    { label: "Finance",         Icon: DollarSign  },
  CRICKET_HEAD:    { label: "Cricket Head",    Icon: Trophy      },
  VOLLEYBALL_HEAD: { label: "Volleyball Head", Icon: Volleyball  },
  CAPTAIN:         { label: "Captain",         Icon: Award       },
  PLAYER:          { label: "Player",          Icon: UserIcon    },
  UMPIRE:          { label: "Umpire",          Icon: Gavel       },
  ADMIN:           { label: "Admin",           Icon: ShieldCheck }
};

const ROLE_PRIORITY = {
  PRESIDENT: 0, VICE_PRESIDENT: 1, ADMIN: 2, CAPTAIN: 3,
  CRICKET_HEAD: 4, VOLLEYBALL_HEAD: 4, FINANCE_HEAD: 5,
  UMPIRE: 6, PLAYER: 9
};

function normalizeRoles(rawRoles) {
  const list = (rawRoles || []).filter(r => r !== "MEMBER");
  if (list.length === 0) return ["PLAYER"];
  return list.sort((a, b) => (ROLE_PRIORITY[a] ?? 99) - (ROLE_PRIORITY[b] ?? 99));
}

function teamPillKind(team) {
  const name = String(team?.name || "");
  if (/\bclub\b/i.test(name)) return "club";
  return team?.sport === "Cricket" ? "cricket" : "volleyball";
}

function teamPillIcon(team) {
  if (/\bclub\b/i.test(String(team?.name || ""))) return Trophy;
  return team?.sport === "Cricket" ? UsersIcon : Volleyball;
}

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function MemberCard({
  member, teams = [], primaryTeam = null,
  canUpdate = false, canDelete = false,
  onStats, onEdit, onRemove,
  showStats = true,
  showVerified = true
}) {
  const roles = normalizeRoles(member.roles);
  const badgeLogoSrc = primaryTeam ? teamLogoSrc(primaryTeam) : null;

  return (
    <article className={`mcard${!member.active ? " mcard--inactive" : ""}`}>
      {/* Left — amber painted-brush avatar panel */}
      <div className="mcard-hero">
        <div className="mcard-hero-paint" aria-hidden="true">
          <svg viewBox="0 0 200 260" preserveAspectRatio="none">
            <defs>
              <linearGradient id="mc-paint" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0"   stopColor="#f4a437"/>
                <stop offset="0.5" stopColor="#e8871f"/>
                <stop offset="1"   stopColor="#c15912"/>
              </linearGradient>
            </defs>
            <g fill="url(#mc-paint)">
              {/* Diagonal brush strokes */}
              <path d="M -20 60  L 210 20   L 220 90  L -30 130 Z"/>
              <path d="M -20 150 L 220 110  L 230 200 L -20 240 Z"/>
              {/* Splatter dots */}
              <circle cx="15"  cy="200" r="4"/>
              <circle cx="35"  cy="220" r="2"/>
              <circle cx="180" cy="35"  r="3"/>
              <circle cx="200" cy="55"  r="1.8"/>
            </g>
            <g fill="#0a1832" opacity="0.6">
              <path d="M -10 180 L 220 130 L 220 260 L -10 260 Z"/>
            </g>
          </svg>
        </div>

        <div className="mcard-hero-photo">
          {member.avatarUpdatedAt ? (
            <Avatar
              member={member}
              size={220}
              style={{ width: "100%", height: "100%", borderRadius: 0, background: "transparent", objectFit: "cover" }}
            />
          ) : (
            <span className="mcard-hero-initials">{initialsOf(member.name)}</span>
          )}
        </div>

        {badgeLogoSrc && (
          <div className="mcard-hero-badge">
            <img src={badgeLogoSrc} alt={primaryTeam?.name || ""} crossOrigin="anonymous"/>
          </div>
        )}
      </div>

      {/* Middle — name, contact, pills */}
      <div className="mcard-body">
        <div className="mcard-name-row">
          <h3 className="mcard-name">{member.name}</h3>
          {showVerified && member.active && (
            <span className="mcard-verified">
              <BadgeCheck size={14}/> VERIFIED
            </span>
          )}
        </div>
        <div className="mcard-underline" aria-hidden="true"/>

        <div className="mcard-contact">
          <div className="mcard-contact-row">
            <span className="mcard-contact-ico"><Mail size={16}/></span>
            <span className="mcard-contact-txt">{member.email || "—"}</span>
          </div>
          <div className="mcard-contact-row">
            <span className="mcard-contact-ico"><PhoneIcon size={16}/></span>
            <span className="mcard-contact-txt">{member.phone || "—"}</span>
          </div>
        </div>

        <div className="mcard-divider" aria-hidden="true"/>

        <div className="mcard-tags">
          {roles.map(r => {
            const meta = ROLE_META[r] || { label: r, Icon: UserIcon };
            const Icon = meta.Icon;
            return (
              <span key={r} className={`mcard-tag mcard-tag--role mcard-tag--${r}`}>
                <span className="mcard-tag-ico"><Icon size={12}/></span>
                {meta.label.toUpperCase()}
              </span>
            );
          })}
          {teams.map(t => {
            const Icon = teamPillIcon(t);
            const kind = teamPillKind(t);
            return (
              <span key={t.id} className={`mcard-tag mcard-tag--team mcard-tag--${kind}`}>
                <span className="mcard-tag-ico"><Icon size={12}/></span>
                {String(t.name).toUpperCase()}
              </span>
            );
          })}
        </div>
      </div>

      {/* Right — action buttons */}
      <div className="mcard-actions">
        {showStats && (
          <button
            type="button"
            className="mcard-action"
            onClick={onStats}
            disabled={!onStats}
          >
            <BarChart3 size={22}/>
            <span>STATS</span>
          </button>
        )}
        {canUpdate && (
          <button
            type="button"
            className="mcard-action"
            onClick={onEdit}
          >
            <Pencil size={22}/>
            <span>EDIT</span>
          </button>
        )}
        {canDelete && member.active && (
          <button
            type="button"
            className="mcard-action mcard-action--danger"
            onClick={onRemove}
            aria-label="Remove member"
          >
            <Trash2 size={22}/>
            <span>REMOVE</span>
          </button>
        )}
      </div>
    </article>
  );
}
